import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import styled, { css } from 'styled-components';
import { themeVar } from '@/styles/theme';
import Label from '../label';
import { ErrorText } from '@/components/basicComponents/input';

const getTextareaSize = (
  size: TextareaProps['size'],
  styleType: TextareaProps['styleType'],
  limitCharactersTo: TextareaProps['limitCharactersTo']
) => {
  let paddingRight = '0px';
  let paddingLeft = '0px';

  if (styleType === 'magicWand') {
    paddingLeft = '50px';
  }

  switch (size) {
    case 'sm':
      return css`
        padding: 8px ${`calc(5px + ${paddingRight})`} 8px ${paddingLeft ? `calc(5px + ${paddingLeft})` : '5px'};
        border-radius: ${!!limitCharactersTo ? '3px 3px 0 0' : '3px'};
        height: 80px;
      `;
    case 'md':
      return css`
        padding: 10px ${`calc(10px + ${paddingRight})`} 10px ${`calc(10px + ${paddingLeft})`};
        border-radius: ${!!limitCharactersTo ? '4px 4px 0 0' : '4px'};
        height: 120px;
      `;
    case 'lg':
      return css`
        padding: 10px ${`calc(12px + ${paddingRight})`} 10px ${`calc(12px + ${paddingLeft})`};
        border-radius: ${!!limitCharactersTo ? '5px 5px 0 0' : '5px'};
        height: 200px;
      `;
    default:
      return '';
  }
};

const getTextareaStyleType = (styleType: TextareaProps['styleType']) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('layer.1')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.1')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'form':
      return css`
        background-color: ${themeVar('layer.2')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.2')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'success':
      return css`
        background-color: #efffea;
        border: 1px solid ${themeVar('context.success')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #efffea inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'error':
      return css`
        background-color: #ffeaec;
        border: 1px solid ${themeVar('context.error')};
        color: #2e2e2e;

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #ffeaec inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'disabled':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.disabled')};
      `;
    case 'magicWand':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.base')};
      `;
    default:
      return '';
  }
};

const getFontProperties = (size: TextareaProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        font-size: 13px;
        line-height: 18.2px;
      `;
    case 'md':
      return css`
        font-size: 16px;
        line-height: 22.4px;
      `;
    case 'lg':
      return css`
        font-size: 19px;
        line-height: 26.6px;
      `;
    default:
      return '';
  }
};

const charactersRemainderFontSizes = {
  sm: css`
    font-size: 11px;
    line-height: 15.4px;
    padding: 5px 8px;
  `,
  md: css`
    font-size: 13px;
    line-height: 18.2px;
    padding: 6px 10px;
  `,
  lg: css`
    font-size: 16px;
    line-height: 22.4px;
    padding: 8px 12px;
  `
};

const charactersStyleType = {
  default: css`
    background-color: ${themeVar('layer.2')};
    border: 1px solid ${themeVar('border.base')};
  `,
  form: css`
    background-color: ${themeVar('layer.2')};
    border: 1px solid ${themeVar('border.base')};
  `,
  success: css`
    background-color: #efffea;
    border: 1px solid ${themeVar('context.success')};
  `,
  error: css`
    background-color: #ffeaec;
    border: 1px solid ${themeVar('context.error')};
  `,
  disabled: css`
    background-color: ${themeVar('action.disabled')};
    border: 1px solid ${themeVar('border.base')};
  `,
  magicWand: css`
    background-color: ${themeVar('action.disabled')};
    border: 1px solid ${themeVar('border.base')};
  `
};

interface TextareaProps {
  value?: string;
  onChange?: (event?: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onKeyUp?: (event?: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  ref?: React.LegacyRef<HTMLInputElement>;
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'form' | 'success' | 'error' | 'disabled' | 'magicWand';
  width?: string;
  limitCharactersTo?: number;
  description?: string;
  errorText?: string;
  label?: string;
  tooltip?: string;
  tooltipWidth?: number;
  isRequiredField?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  'data-testid'?: string;
  'data-testid-label'?: string;
  'data-testid-error'?: string;
}

const Textarea = ({
  value,
  onChange,
  onClick,
  onKeyUp,
  onBlur,
  ref,
  size = 'lg',
  styleType = 'default',
  width = '100%',
  limitCharactersTo,
  description,
  errorText,
  label,
  tooltip,
  tooltipWidth = 0,
  isRequiredField = false,
  placeholder = '',
  autoFocus = false,
  autoComplete,
  className,
  'data-testid': testId,
  'data-testid-label': labelTestId,
  'data-testid-error': errorTestId
}: TextareaProps) => {
  return (
    <TextareaWrapper>
      {label && (
        <Label required={isRequiredField} size={size} tooltip={tooltip} tooltipWidth={tooltipWidth} data-testid={labelTestId}>
          {label}
        </Label>
      )}
      <StyledTextareaWrapper>
        <StyledTextarea
          data-testid={testId}
          value={value}
          onChange={onChange}
          onClick={onClick}
          onKeyUp={onKeyUp}
          onBlur={onBlur}
          ref={ref}
          disabled={styleType === 'disabled' || styleType === 'magicWand'}
          placeholder={placeholder}
          width={width}
          size={size}
          styleType={styleType}
          limitCharactersTo={limitCharactersTo}
          className={className}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
        />
        {styleType === 'magicWand' && (
          <MagicWandIconWrapper size={size}>
            <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
          </MagicWandIconWrapper>
        )}
      </StyledTextareaWrapper>
      {!!limitCharactersTo && (
        <CharactersRemainder size={size} styleType={styleType}>
          <AmountCharacters>{limitCharactersTo - value?.length}</AmountCharacters> Characters Remaining
        </CharactersRemainder>
      )}
      {description && <Description>{description}</Description>}
      {errorText && <ErrorText size={size} data-testid={errorTestId}>{errorText}</ErrorText>}
    </TextareaWrapper>
  );
};

export default Textarea;

const TextareaWrapper = styled.div`
  width: 100%;
`;

const StyledTextareaWrapper = styled.div`
  position: relative;
  font-size: 0;
`;

const StyledTextarea = styled.textarea<TextareaProps>`
  font-family: Blinker;
  font-style: normal;
  font-weight: 400;
  resize: none;
  width: ${({ width }) => width};
  cursor: ${({ styleType }) => (styleType === 'disabled' ? 'not-allowed' : 'auto')};

  ${({ size, styleType, limitCharactersTo }) => getTextareaSize(size, styleType, limitCharactersTo)}
  ${({ styleType }) => getTextareaStyleType(styleType)}
  ${({ size }) => getFontProperties(size)}

  &:focus {
    outline: none;
  }

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CharactersRemainder = styled.div<{ size: TextareaProps['size']; styleType: TextareaProps['styleType'] }>`
  font-weight: 400;
  border-radius: 0px 0px 4px 4px;
  color: ${themeVar('font.weak')};

  ${({ size }) => charactersRemainderFontSizes[size]}
  ${({ styleType }) => charactersStyleType[styleType]}
  border-top: none;
`;

const AmountCharacters = styled.span`
  font-weight: 700;
`;

const MagicWandIconWrapper = styled.div<{ size: TextareaProps['size'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};
  height: 80%;
`;

const Description = styled.span`
  font-weight: 300;
  font-size: 13px;
  line-height: 18.2px;
  color: ${themeVar('font.weak')};
`;
